/** The `groups` area: the Groups page, and a group's page with its members and
 *  access. Owned by the stage 2 slot people (docs/I18N.md §11). The keys read
 *  in the order a person meets them: the Groups page, a group's page, its
 *  membership surface. */
export const groups = {
  // --- The Groups page (page.tsx, groups-view.ts) ---
  'groups.list.title': 'Groups',
  /** The header's line under the title when there is no group yet. */
  'groups.list.none': 'the whos',
  'groups.list.count': { one: '{count} group', other: '{count} groups' },
  'groups.list.sortBy': 'Sort by',
  'groups.list.sortLabel': 'Sort groups',
  'groups.sort.name': 'Name (A–Z)',
  'groups.sort.work': 'Most shared items',
  'groups.sort.members': 'Most members',
  'groups.sort.recent': 'Recently added',
  'groups.list.empty':
    'No groups yet. A group is a WHO - it holds no work of its own; you grant work into it and it rides its board. Create one to get started.',
  /** The counts on a card and in a group's header. */
  'groups.count.members': { one: '{count} member', other: '{count} members' },
  'groups.count.sharedItems': { one: '{count} shared item', other: '{count} shared items' },
  'groups.count.views': { one: '{count} board or view', other: '{count} boards and views' },
  'groups.count.open': '{count} open',

  // --- A group's page ([groupId]/page.tsx) ---
  'groups.page.definition':
    'A group is a set of people. It holds no work of its own: it takes seats on contracts, and boards, views and work are granted to it.',
  'groups.page.description': 'Description',
  'groups.page.descriptionPlaceholder': 'What is this group for?',
  'groups.page.views': 'Boards and views',
  'groups.page.noViews':
    'No board or view is shared with this group. A board or a view reaches it once the group is in its audience.',
  /** A view row's kind. */
  'groups.viewKind.map': 'map',
  'groups.viewKind.board': 'board',
  'groups.viewKind.lens': 'view',
  'groups.page.work': 'Work',
  'groups.page.nothingOpen': 'Nothing open. Every contract granted to this group is closed.',
  'groups.page.noWork': 'No work is granted to this group yet.',
  /** Under the capped work list: what it does not show. */
  'groups.page.moreOpen': '{count} more open',
  'groups.page.done': '{count} closed',
  'groups.page.cancelled': '{count} cancelled',
  'groups.page.seating': 'Who can assign this group directly',

  // --- Membership ([groupId]/group-access.tsx, group-admin-sections.tsx) ---
  'groups.members.title': 'Members',
  'groups.parents.title': 'Nested under',
  /** A role as the role menus offer it, with what it allows. */
  'groups.role.option.viewer': 'Viewer',
  'groups.role.option.member': 'Member',
  'groups.role.option.owner': 'Owner',
  'groups.role.hint.viewer': 'can see',
  'groups.role.hint.member': 'can work',
  'groups.role.hint.owner': 'can manage',
  /** A role as a row or a tag shows it: lowercase, the stored word. */
  'groups.role.viewer': 'viewer',
  'groups.role.member': 'member',
  'groups.role.owner': 'owner',
  /** The roster's headings, strongest rank first. */
  'groups.rank.owners': 'Owners',
  'groups.rank.members': 'Members',
  'groups.rank.viewers': 'Viewers',
  /** A membership waiting for an answer: its tag and its acts. */
  'groups.pending.asksToNest': 'asks to nest · {role}',
  'groups.pending.askedToJoin': 'asked to join · {role}',
  'groups.pending.pending': 'pending · {role}',
  'groups.pending.accept': 'Accept',
  'groups.pending.decline': 'Decline',
  'groups.pending.withdraw': 'Withdraw',
  /** The add affordance on the page, and the picker it opens. */
  'groups.add.member': 'Add a person or a group',
  'groups.add.parent': 'Nest under a group',
  'groups.add.cancel': 'Cancel',
  'groups.add.memberPlaceholder': 'Add people and groups…',
  'groups.add.parentPlaceholder': 'Nest under a group…',
  'groups.add.noParent': 'No other group to nest under.',
  /** Where a member's access comes from. */
  'groups.source.direct': 'directly',
  'groups.source.via': 'via {name}',
  'groups.source.nested': 'nested',
  'groups.member.roleFor': 'Role for {name}',
  'groups.member.directRole': 'Their direct role here (they reach {role} through a nested group)',
  'groups.member.remove': 'Remove {name}',
  'groups.parent.leave': 'Leave {name}',
  'groups.members.empty': 'No one yet.',
  'groups.parents.empty': 'Nested under nothing - this group stands on its own.',
} as const;
